import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../buttons/Button';
import { GoogleFontIcon } from '../icons/GoogleFontIcon';
import { Textarea } from '../textarea/Textarea';

import { DocumentContextChips, PageContextChips } from './PageContextChips';
import type {
  AiAssistantDocumentContext,
  AiAssistantPageContext,
} from './types';

import styles from './AiAssistantComposer.module.scss';

type AiAssistantComposerProps = {
  availableDocuments?: AiAssistantDocumentContext[];
  currentPage?: AiAssistantPageContext | null;
  disabled?: boolean;
  onSend: (
    message: string,
    pageContexts: AiAssistantPageContext[],
    documentContexts: AiAssistantDocumentContext[]
  ) => boolean | void | Promise<boolean | void>;
};

export function AiAssistantComposer({
  availableDocuments = [],
  currentPage,
  disabled = false,
  onSend,
}: AiAssistantComposerProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const [pageContexts, setPageContexts] = useState<AiAssistantPageContext[]>(
    []
  );
  const [documentContexts, setDocumentContexts] = useState<
    AiAssistantDocumentContext[]
  >([]);
  const trimmedDraft = draft.trim();
  const canAddCurrentPage =
    currentPage != null &&
    !pageContexts.some((page) => page.href === currentPage.href);
  const documentsToAdd = availableDocuments.filter(
    (document) =>
      !documentContexts.some((attached) => attached.id === document.id)
  );
  const canAddDocuments = documentsToAdd.length > 0;

  function addCurrentPage() {
    if (disabled || !canAddCurrentPage || currentPage == null) {
      return;
    }

    setPageContexts((current) =>
      current.some((page) => page.href === currentPage.href)
        ? current
        : [...current, currentPage]
    );
  }

  function addAvailableDocuments() {
    if (disabled || !canAddDocuments) {
      return;
    }

    setDocumentContexts((current) => {
      const attachedIds = new Set(current.map((document) => document.id));
      const next = documentsToAdd.filter(
        (document) => !attachedIds.has(document.id)
      );
      return next.length === 0 ? current : [...current, ...next];
    });
  }

  async function sendDraft() {
    if (disabled || !trimmedDraft) {
      return;
    }

    const accepted = await onSend(trimmedDraft, pageContexts, documentContexts);

    if (accepted !== false) {
      setDraft('');
      setPageContexts([]);
      setDocumentContexts([]);
    }
  }

  return (
    <form
      className={styles['root']}
      data-testid="ai-assistant-composer-form"
      onKeyDown={(event) => {
        if (
          event.key === 'Enter' &&
          !event.shiftKey &&
          !event.nativeEvent.isComposing &&
          event.target instanceof HTMLTextAreaElement
        ) {
          event.preventDefault();
          void sendDraft();
        }
      }}
      onSubmit={(event) => {
        event.preventDefault();
        void sendDraft();
      }}
    >
      <div className={styles['composer']}>
        <Textarea
          autosize
          data-testid="ai-assistant-composer"
          disabled={disabled}
          maxRows={8}
          minRows={1}
          onChange={setDraft}
          placeholder={t('app.aiAssistant.composerPlaceholder')}
          value={draft}
        />
        <div className={styles['composer-actions']}>
          <div className={styles['composer-context']}>
            <div className={styles['composer-tools']}>
              <Button
                data-testid="ai-assistant-add-page"
                disabled={disabled || !canAddCurrentPage}
                icon={<GoogleFontIcon name="add" solid />}
                title={t('app.aiAssistant.addCurrentPage')}
                type="button"
                variant="text"
                onClick={addCurrentPage}
              >
                {t('app.aiAssistant.addCurrentPage')}
              </Button>
              {availableDocuments.length > 0 ? (
                <Button
                  data-testid="ai-assistant-add-documents"
                  disabled={disabled || !canAddDocuments}
                  icon={<GoogleFontIcon name="add" solid />}
                  title={t('app.aiAssistant.addDocuments', {
                    count: availableDocuments.length,
                  })}
                  type="button"
                  variant="text"
                  onClick={addAvailableDocuments}
                >
                  {t('app.aiAssistant.addDocuments', {
                    count: availableDocuments.length,
                  })}
                </Button>
              ) : null}
            </div>
            <PageContextChips
              items={pageContexts}
              onRemove={
                disabled
                  ? undefined
                  : (href) =>
                      setPageContexts((current) =>
                        current.filter((page) => page.href !== href)
                      )
              }
            />
            <DocumentContextChips
              items={documentContexts}
              onRemove={
                disabled
                  ? undefined
                  : (id) =>
                      setDocumentContexts((current) =>
                        current.filter((document) => document.id !== id)
                      )
              }
            />
          </div>
          <Button
            disabled={disabled || !trimmedDraft}
            icon={<GoogleFontIcon name="send" solid />}
            title={t('app.aiAssistant.sendMessage')}
            type="submit"
            data-testid="ai-assistant-send"
          />
        </div>
      </div>
    </form>
  );
}
